import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Card, CardHeader, CardBody, Col, Row, Badge, ButtonGroup, Button, Container,
} from 'reactstrap';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon';
import ViewGridIcon from 'mdi-react/ViewGridIcon';
import ViewListIcon from 'mdi-react/ViewListIcon';
import { isDarkTheme } from '../../../../../shared/helpers/themeTableStyles';
import useData from './useData';
import PageHeader from '../../../../../shared/components/PageHeader';
import CollapsibleSection from '../../../../Budget/components/form/shared/CollapsibleSection';

// Onboarding tab hashes, aligning to /customer/onboarding#<tab> (see Onboarding/home.jsx TabPane ids).
// AMC has no dedicated onboarding tab today, so it links to the Amazon tab instead.
const ONBOARDING_TAB = {
  amazon: 'amazon',
  amc: 'amazon',
  dsp: 'dsp',
  walmart: 'walmart',
  google: 'google',
  meta: 'meta',
  tiktok: 'tiktok',
  target: 'target',
  microsoft: 'microsoft',
};

const CARD_STYLE = {
  height: '100%',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '6px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
};

// Status color -> human readable label + Bootstrap badge color + header background/text
// color, following the convention documented in notes/API_STATUS_DASHBOARD_GUIDE.md
// (updated scheme: `yellow` has been retired; `gray` now covers both "not yet configured"
// and "gate explicitly off" — neither is a problem worth flagging; `red` is reserved for a
// live test failure on something that IS configured/on.)
// headerBg mirrors the exact Bootstrap badge color used for the same status, so the card
// header banner and its status badges always match.
const STATUS_META = {
  green: {
    label: 'Active', badge: 'success', headerBg: '#1e9e73', headerText: '#ffffff',
  },
  blue: {
    label: 'Configured - Not Tested', badge: 'info', headerBg: '#2f8fd6', headerText: '#ffffff',
  },
  gray: {
    label: 'Not Active', badge: 'secondary', headerBg: '#6c757d', headerText: '#ffffff',
  },
  red: {
    label: 'Test Failed', badge: 'danger', headerBg: '#c9192f', headerText: '#ffffff',
  },
  black: {
    label: 'Unused', badge: 'dark', headerBg: '#343a40', headerText: '#ffffff',
  },
};

// When a platform exposes multiple statuses (e.g. Amazon AD-API + SP-API), the card header
// shows the single "worst" one so the color reflects the platform's overall readiness.
// Priority (most to least severe): red > gray > blue > green > black.
const STATUS_SEVERITY = ['red', 'gray', 'blue', 'green', 'black'];

function getOverallStatus(statuses) {
  const values = (statuses || []).map(s => s.status).filter(Boolean);
  if (values.length === 0) return '';
  return STATUS_SEVERITY.find(s => values.includes(s)) || values[0];
}

function StatusBadge({ status, label }) {
  if (!status) return null;
  const meta = STATUS_META[status] || { label: status, badge: 'secondary' };
  return (
    <Badge color={meta.badge} className="mb-1" style={{ marginLeft: '6px' }}>
      {label ? `${label}: ` : ''}{meta.label}
    </Badge>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
  label: PropTypes.string,
};

StatusBadge.defaultProps = {
  status: '',
  label: '',
};

// "*_ad_api_status" style gate settings are toggles, not free-text values — a value of '0'
// means the platform's AD-API gate is explicitly turned OFF, which is a broken/incomplete
// state even though the setting technically "has a value". Treat that as not-configured.
const isAdApiStatusGate = settingKey => /ad[\s_-]?api[\s_-]?status/i.test(settingKey || '');

function SettingsChecklist({ settings }) {
  if (!settings || settings.length === 0) {
    return <small className="text-muted">No settings tracked for this platform yet.</small>;
  }
  // Icon convention:
  // - Configured (any setting), excluding "*_ad_api_status" gates explicitly set to '0': GREEN checkmark
  // - Not configured & required (isRequired: true), or an "*_ad_api_status" gate set to '0': RED X
  // - Not configured & optional (isRequired: false/undefined): YELLOW O
  const renderIcon = (setting) => {
    const isDisabledGate = isAdApiStatusGate(setting.settingKey) && setting.value === '0';

    if (setting.isConfigured && !isDisabledGate) {
      return <CheckIcon size={16} style={{ color: 'green', flexShrink: 0 }} />;
    }
    if (setting.isRequired || isDisabledGate) {
      return <CloseIcon size={16} style={{ color: 'red', flexShrink: 0 }} />;
    }
    return <CheckboxBlankCircleOutlineIcon size={16} style={{ color: '#e6c200', flexShrink: 0 }} />;
  };

  return (
    <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
      {settings.map((setting) => {
        const isDisabledGate = isAdApiStatusGate(setting.settingKey) && setting.value === '0';
        return (
          <li
            key={setting.settingKey}
            title={setting.value || ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 0',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {renderIcon(setting)}
            <span style={{ marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong>{setting.settingKey}</strong>
              {setting.isConfigured && setting.value ? <span>: {setting.value}</span> : ''}
              {isDisabledGate && <span className="text-danger"> (disabled)</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

SettingsChecklist.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.array,
};

SettingsChecklist.defaultProps = {
  settings: [],
};

function PlatformDetails({
  platformKey, title, settings, notes,
}) {
  const onboardingTab = ONBOARDING_TAB[platformKey];
  return (
    <>
      <SettingsChecklist settings={settings} />
      {notes && (
        <div className="mt-3">
          <CollapsibleSection title="Setup Notes" defaultOpen={false}>
            {notes}
            {onboardingTab && (
              <div className="mt-3">
                <Link className="btn btn-sm btn-outline-primary" to={`/customer/onboarding#${onboardingTab}`}>
                  Go to {title} Onboarding
                </Link>
              </div>
            )}
          </CollapsibleSection>
        </div>
      )}
    </>
  );
}

PlatformDetails.propTypes = {
  platformKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.array,
  notes: PropTypes.node,
};

PlatformDetails.defaultProps = {
  settings: [],
  notes: null,
};

function PlatformCardHeader({ title, statuses, allRequiredConfigured }) {
  const theme = useSelector(state => state.theme);
  const darkMode = isDarkTheme(theme);
  const overallStatus = getOverallStatus(statuses);
  const meta = STATUS_META[overallStatus] || { headerBg: '#eeeeee', headerText: '#1c1c1c' };
  return (
    <>
      <CardHeader
        style={{
          backgroundColor: meta.headerBg,
          color: meta.headerText,
          borderBottom: 'none',
        }}
      >
        <h4 className="bold-text text-center mb-0" style={{ color: meta.headerText }}>{title}</h4>
      </CardHeader>
      <div
        className="d-flex justify-content-end align-items-center flex-wrap px-3 py-2"
        style={{
          backgroundColor: darkMode ? '#2a2d3a' : '#f7f7f7',
          borderBottom: darkMode ? '1px solid #434651' : '1px solid #eeeeee',
        }}
      >
        {allRequiredConfigured !== null && (
          <Badge color={allRequiredConfigured ? 'success' : 'secondary'} className="mb-1">
            {allRequiredConfigured ? 'Fully Configured' : 'Incomplete Setup'}
          </Badge>
        )}
        {statuses.map(s => (
          <StatusBadge key={`${title}-${s.label || s.status}`} status={s.status} label={s.label} />
        ))}
      </div>
    </>
  );
}

PlatformCardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
};

PlatformCardHeader.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
};

function PlatformLoadingSpinner({ title }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-4">
      <span className="spinner-border" role="status" aria-hidden="true" />
      <small className="text-muted mt-2">Loading {title} status...</small>
    </div>
  );
}

PlatformLoadingSpinner.propTypes = {
  title: PropTypes.string.isRequired,
};

function PlatformCard(props) {
  const {
    title, statuses, allRequiredConfigured, loading,
  } = props;
  const theme = useSelector(state => state.theme);
  const cardBodyStyle = { color: isDarkTheme(theme) ? '#dddddd' : '#212529' };
  return (
    <Col md={6} lg={4} className="mb-4">
      <Card style={CARD_STYLE}>
        <PlatformCardHeader title={title} statuses={statuses} allRequiredConfigured={allRequiredConfigured} />
        <CardBody style={cardBodyStyle}>
          {loading ? <PlatformLoadingSpinner title={title} /> : <PlatformDetails {...props} />}
        </CardBody>
      </Card>
    </Col>
  );
}

PlatformCard.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
  loading: PropTypes.bool,
};

PlatformCard.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
  loading: false,
};

function PlatformListRow(props) {
  const {
    title, statuses, allRequiredConfigured, loading,
  } = props;
  const theme = useSelector(state => state.theme);
  const cardBodyStyle = { color: isDarkTheme(theme) ? '#dddddd' : '#212529' };
  return (
    <Card style={{ ...CARD_STYLE, height: 'auto' }} className="mb-3">
      <PlatformCardHeader title={title} statuses={statuses} allRequiredConfigured={allRequiredConfigured} />
      <CardBody style={cardBodyStyle}>
        {loading ? <PlatformLoadingSpinner title={title} /> : <PlatformDetails {...props} />}
      </CardBody>
    </Card>
  );
}

PlatformListRow.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
  loading: PropTypes.bool,
};

PlatformListRow.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
  loading: false,
};

const AMAZON_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Authorize the Amazon Advertising (AD) API via the &quot;Onboarding Links&quot; panel above.</li>
    <li>Authorize the Amazon Selling Partner (SP) API via the SP-API onboarding link (requires an Entity ID).</li>
    <li>Enable FBA if this account fulfills via Amazon.</li>
  </ol>
);

const AMC_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the AMC Instance ID for this account&apos;s Amazon Marketing Cloud instance.</li>
    <li>Provide the AMC Manager Account ID if this instance is shared under a manager account.</li>
    <li>Email help@limitless.tech once these are ready so we can validate access.</li>
  </ol>
);

const DSP_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the Amazon DSP Account ID and DSP Account Name for this account.</li>
    <li>Add Limitless as an authorized advertiser/agency on the DSP seat.</li>
  </ol>
);

const WALMART_NOTES = (
  <>
    <span>Three Steps (only one of steps 3 or step 4 required) to setup your Walmart Access and APIs:</span>
    <ol type="1">
      <li>
        Add User admaster@limiltess.tech in Advertising Console access with Write access.<br />
        &nbsp;&nbsp;Please follow the directions in Walmart Connect (https://advertisinghelp.walmart.com/s/guides?article=000009334)<br />
        &nbsp;&nbsp;*** see the &quot;Admin Management&quot; section with subsection &quot;Adding a user to your account&quot; *** that will have instructions to add admaster@limitless.tech as a user with write access.
      </li>
      <br />
      <li>
        Add Limitless as a API Partner, which will give us Walmart Connect API (AD-API) access.<br />
        &nbsp;&nbsp;After logging into advertising.walmart.com do the following:<br />
        &nbsp;&nbsp;&nbsp;&nbsp;Click on &quot;Account&quot; in the upper right, and click &quot;Admin&quot; from the dropdown<br />
        &nbsp;&nbsp;&nbsp;&nbsp;Find &quot;API Partner - Advertiser level&quot; section and click &quot;Add Partner&quot;<br />
        &nbsp;&nbsp;&nbsp;&nbsp;Select &quot;Limitless&quot; from the partner name dropdown and &quot;Write Access&quot; from the access type dropdown. Then Click &quot;Add Partner&quot;
      </li>
      <br />
      <li>
        (SELLER ONLY) Setup Marketplace API Client ID and Client Secret. Email Client ID and Client Secret to help@limiltess.tech<br />
        &nbsp;&nbsp;Please follow the directions in Walmart Seller Help (https://sellerhelp.walmart.com/seller/s/guide?article=000011076&language=en_US) to generate API Keys for Limitless. Limitless needs to have &quot;Full Access&quot;<br />
      </li>
      <br />
      <li>
        (SUPPLIER ONLY) Setup Supplier API Client ID and Client Secret. Email Client ID and Client Secret to help@limiltess.tech<br />
        &nbsp;&nbsp;Coming Soon<br />
      </li>
    </ol>
  </>
);

const GOOGLE_NOTES = (
  <>
    <span>There are two different models we support:</span>
    <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
      <li>
        Connecting direclty to the customer ad account:<br />
        + customer runs though the Google Onboarding flow to grant premission<br />
        + Customer must identifiy the Google Customer ID that they want us to use as there can be muiltiple customer accounts<br />
        + Open question on how they supply/onboard Merchant Center ID so we can do Shopping/Performance Max feeds
      </li>
      <li>
        Give our Google Manager access to their ad account:<br />
        + Assign Limitless AdMaster as a Manger (524-067-9842) in your acocunt<br />
        + Customer can provide the Google Customer ID, or we can pick it from the list returned.<br />
        + After Limitless is assigned as a Manager, we can use the Onboarding URL to get a Google Refresh Token<br />
        + Open question on how they supply/onboard Merchant Center ID so we can do Shopping/Performance Max feeds
      </li>
    </ol>
  </>
);

const META_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the Meta Ad Account ID and grant Limitless partner access via Business Manager.</li>
    <li>Provide the Page ID, Pixel ID, Catalog ID, Instagram Account ID and Product Set ID as applicable.</li>
  </ol>
);

const TIKTOK_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the TikTok Advertiser ID and Business Center ID.</li>
    <li>Provide the Catalog ID, Pixel ID and Product Set ID if running catalog/conversion campaigns.</li>
  </ol>
);

const TARGET_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the Target Vendor ID (and RMS Account ID / Supplier ID depending on partnership type).</li>
    <li>Confirm the account currency and time zone for reporting accuracy.</li>
  </ol>
);

const MICROSOFT_NOTES = (
  <ol style={{ paddingLeft: '18px', marginBottom: 0 }}>
    <li>Provide the Microsoft Advertising Account ID.</li>
    <li>Provide the Client ID, Object ID and Tenant ID from the Azure AD app registration used for API access.</li>
  </ol>
);

export default function AccountStatusWithContext() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const [loading, results, error] = useData(selectedSeller);
  const [viewMode, setViewMode] = useState('cards');

  const amazon = results?.amazon || {};
  const amc = results?.amc || {};
  const dsp = results?.dsp || {};
  const walmart = results?.walmart || {};
  const google = results?.google || {};
  const meta = results?.meta || {};
  const tiktok = results?.tiktok || {};
  const target = results?.target || {};
  const microsoft = results?.microsoft || {};

  const platforms = [
    {
      platformKey: 'amazon',
      title: 'Amazon',
      statuses: [
        { status: amazon.adApiStatus, label: 'AD-API' },
        { status: amazon.spApiStatus, label: 'SP-API' },
      ],
      settings: amazon.settings,
      notes: AMAZON_NOTES,
      allRequiredConfigured: amazon.allRequiredConfigured,
    },
    {
      platformKey: 'amc',
      title: 'AMC',
      statuses: [{ status: amc.status }],
      settings: amc.settings,
      notes: AMC_NOTES,
      allRequiredConfigured: amc.allRequiredConfigured,
    },
    {
      platformKey: 'dsp',
      title: 'DSP',
      statuses: [{ status: dsp.status }],
      settings: dsp.settings,
      notes: DSP_NOTES,
      allRequiredConfigured: dsp.allRequiredConfigured,
    },
    {
      platformKey: 'walmart',
      title: 'Walmart',
      statuses: [
        { status: walmart.adApiStatus, label: 'AD-API' },
        { status: walmart.mpApiStatus, label: 'MP-API' },
        { status: walmart.suApiStatus, label: 'SU-API' },
      ],
      settings: walmart.settings,
      notes: WALMART_NOTES,
      allRequiredConfigured: walmart.allRequiredConfigured,
    },
    {
      platformKey: 'google',
      title: 'Google',
      statuses: [{ status: google.status }],
      settings: google.settings,
      notes: GOOGLE_NOTES,
      allRequiredConfigured: google.allRequiredConfigured,
    },
    {
      platformKey: 'meta',
      title: 'Meta',
      statuses: [{ status: meta.status }],
      settings: meta.settings,
      notes: META_NOTES,
      allRequiredConfigured: meta.allRequiredConfigured,
    },
    {
      platformKey: 'tiktok',
      title: 'TikTok',
      statuses: [{ status: tiktok.status }],
      settings: tiktok.settings,
      notes: TIKTOK_NOTES,
      allRequiredConfigured: tiktok.allRequiredConfigured,
    },
    {
      platformKey: 'target',
      title: 'Target',
      statuses: [{ status: target.status }],
      settings: target.settings,
      notes: TARGET_NOTES,
      allRequiredConfigured: target.allRequiredConfigured,
    },
    {
      platformKey: 'microsoft',
      title: 'Microsoft',
      statuses: [{ status: microsoft.status }],
      settings: microsoft.settings,
      notes: MICROSOFT_NOTES,
      allRequiredConfigured: microsoft.allRequiredConfigured,
    },
  ].map(platform => ({ ...platform, loading }));

  const viewToggle = (
    <ButtonGroup>
      <Button
        color="primary"
        outline={viewMode !== 'cards'}
        active={viewMode === 'cards'}
        onClick={() => setViewMode('cards')}
        title="Card view"
      >
        <ViewGridIcon size={18} />
      </Button>
      <Button
        color="primary"
        outline={viewMode !== 'list'}
        active={viewMode === 'list'}
        onClick={() => setViewMode('list')}
        title="List view"
      >
        <ViewListIcon size={18} />
      </Button>
    </ButtonGroup>
  );

  return (
    <Container>
      {error}
      <Row>
        <PageHeader title="Platform Configuration Status" actions={viewToggle}>
          <h5 className="page-subhead subhead">
            &nbsp;&nbsp;<span className="status__label-color status__label-color--green" />=Configured and successful live test.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--blue" />=Configured (and on, if applicable), no live test yet.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--gray" />=Not active — missing a required setting, or turned off.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--red" />=Configured/on, but the live test failed.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--black" />=Not required/used/needed.
          </h5>
          <h5 className="page-subhead subhead">
            &nbsp;&nbsp;<CloseIcon size={16} style={{ color: 'red', verticalAlign: 'text-bottom' }} />=Missing (required setting).
            &nbsp;&nbsp;<CheckboxBlankCircleOutlineIcon size={16} style={{ color: '#e6c200', verticalAlign: 'text-bottom' }} />=Missing (optional setting).
            &nbsp;&nbsp;<CheckIcon size={16} style={{ color: 'green', verticalAlign: 'text-bottom' }} />=Found (configured).
          </h5>
        </PageHeader>
      </Row>
      {viewMode === 'cards' && (
        <Row>
          {platforms.map(platform => (
            <PlatformCard key={platform.platformKey} {...platform} />
          ))}
        </Row>
      )}
      {viewMode === 'list' && (
        <Row>
          <Col md={12}>
            {platforms.map(platform => (
              <PlatformListRow key={platform.platformKey} {...platform} />
            ))}
          </Col>
        </Row>
      )}
    </Container>
  );
}
